import React, { useState, useEffect, useRef, Fragment } from "react";
import { Row, Col, Card, CardBody, Badge, Spinner } from "reactstrap";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { useAccount } from "../../context/AccountContext";
import { strategyLabel } from "../../constants/strategies";
import PageTitleAlt2 from "../../Layout/AppMain/PageTitleAlt2";

const fmt = (n) =>
  n == null ? "—" : new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.abs(n));
const pnlColor = (v) => (v >= 0 ? "#28a745" : "#dc3545");
const pnlFmt   = (v) => { if (v == null) return "—"; return `${v >= 0 ? "+" : "-"}₹${fmt(v)}`; };
const distColor = (v) => v == null ? "#888" : v < 0 ? "#dc3545" : v < 1.5 ? "#fd7e14" : "#28a745";
const fmtDist   = (v) => { if (v == null) return "—"; return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`; };
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
// Handles MM/DD/YYYY (from intent.expiry) → "May 25"
const fmtExpMonth = (d) => {
  if (!d) return "—";
  const parts = d.split("/");
  if (parts.length < 3) return "—";
  const mon = MONTHS[parseInt(parts[0], 10) - 1] || "";
  return `${mon} ${parts[2].slice(2)}`;
};

/* ── All-accounts positions view ── */
const AllAccountsPositions = () => {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get("/dashboard/all-summary")
      .then(r => setSummaries(r.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: 60 }}><Spinner color="primary" /></div>;

  const totalStockPnl   = summaries.reduce((a, s) => a + s.stock_pnl, 0);
  const totalOptionPnl  = summaries.reduce((a, s) => a + s.fo_unrealised_pnl, 0);
  const totalPotential  = summaries.reduce((a, s) => a + s.potential_pnl, 0);
  const totalShort      = summaries.reduce((a, s) => a + s.short_option_value, 0);
  const totalCash       = summaries.reduce((a, s) => a + (s.available_cash || 0), 0);

  return (
    <div style={{ padding: "0 25px 30px" }}>
      {/* Aggregate strip */}
      <Row className="mb-4">
        {[
          { label: "Total Available Cash", value: `Rs.${fmt(totalCash)}`,  color: "#28a745" },
          { label: "Combined P&L",         value: pnlFmt(totalStockPnl + totalOptionPnl), color: pnlColor(totalStockPnl + totalOptionPnl) },
          { label: "Total Stock P&L",      value: pnlFmt(totalStockPnl),  color: pnlColor(totalStockPnl) },
          { label: "Total F&O P&L",        value: pnlFmt(totalOptionPnl), color: pnlColor(totalOptionPnl) },
          { label: "Total Potential P&L",  value: pnlFmt(totalPotential), color: pnlColor(totalPotential) },
          { label: "Short Option Value",   value: `Rs.${fmt(totalShort)}`, color: "#6f42c1" },
        ].map(c => (
          <Col key={c.label}>
            <Card className="mb-2" style={{ borderRadius: 10, border: "1px solid #eee", textAlign: "center" }}>
              <CardBody style={{ padding: "12px 10px" }}>
                <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", marginBottom: 4 }}>{c.label}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: c.color }}>{c.value}</div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Per-account table */}
      <Card style={{ borderRadius: 12, border: "1px solid #eee" }}>
        <CardBody style={{ padding: 0 }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8f9fb" }}>
                  {["Account", "Broker", "Stocks", "Stock P&L", "F&O P&L", "Potential P&L", "Short Value", "Available Cash", "Used Margin", "Options", "Strategies", "Rules"].map(h => (
                    <th key={h} style={{ padding: "11px 14px", textAlign: "left", color: "#555", fontWeight: 600, whiteSpace: "nowrap", borderBottom: "2px solid #eee" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summaries.length === 0 && (
                  <tr><td colSpan={12} style={{ textAlign: "center", padding: 32, color: "#aaa" }}>No accounts found.</td></tr>
                )}
                {summaries.map(s => (
                  <tr key={s.username} style={{ borderTop: "1px solid #f0f0f0" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <td style={{ padding: "10px 14px", fontWeight: 700 }}>{s.username}</td>
                    <td style={{ padding: "10px 14px", color: "#888" }}>{s.broker}</td>
                    <td style={{ padding: "10px 14px" }}>{s.stock_count}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 600, color: pnlColor(s.stock_pnl) }}>{pnlFmt(s.stock_pnl)}</td>
                    <td style={{ padding: "10px 14px", color: pnlColor(s.fo_unrealised_pnl) }}>{pnlFmt(s.fo_unrealised_pnl)}</td>
                    <td style={{ padding: "10px 14px", color: pnlColor(s.potential_pnl) }}>{pnlFmt(s.potential_pnl)}</td>
                    <td style={{ padding: "10px 14px", color: "#6f42c1" }}>Rs.{fmt(s.short_option_value)}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 600, color: s.available_cash != null ? "#28a745" : "#aaa" }}>
                      {s.available_cash != null ? `Rs.${fmt(s.available_cash)}` : "—"}
                    </td>
                    <td style={{ padding: "10px 14px", color: s.used_margin != null ? "#dc3545" : "#aaa" }}>
                      {s.used_margin != null ? `Rs.${fmt(s.used_margin)}` : "—"}
                    </td>
                    <td style={{ padding: "10px 14px" }}>{s.option_positions}</td>
                    <td style={{ padding: "10px 14px" }}>{s.active_strategies > 0 ? <Badge color="success">{s.active_strategies} active</Badge> : "—"}</td>
                    <td style={{ padding: "10px 14px" }}>{s.active_rules > 0 ? <Badge color="primary">{s.active_rules}</Badge> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

// All toggleable columns. Keys match the stock object fields.
const TOGGLE_COLS = [
  { key: "expiry",        label: "Exp Month" },
  { key: "ltp",           label: "LTP" },
  { key: "qty",           label: "Qty (Lots)" },
  { key: "avg_price",     label: "Avg Price" },
  { key: "invested",      label: "Invested" },
  { key: "stock_pnl",     label: "Stock P&L" },
  { key: "option_pnl",    label: "F&O P&L" },
  { key: "potential_pnl", label: "Potential P&L" },
  { key: "premium_rec",   label: "Premium Rec." },
  { key: "options",       label: "Options" },
  { key: "strategy",      label: "Strategy" },
  { key: "bullish_delta",    label: "Bullish Δ" },
  { key: "bearish_delta",    label: "Bearish Δ" },
  { key: "delta_diff",       label: "Delta Diff" },
  { key: "nearest_put_pct",  label: "Nearest Put %" },
  { key: "nearest_call_pct", label: "Nearest Call %" },
  { key: "booked_profit",    label: "Booked Profit" },
];

const DEFAULT_HIDDEN = new Set(["bullish_delta", "bearish_delta", "delta_diff"]);
const DEFAULT_COL_ORDER = TOGGLE_COLS.map(c => c.key);

const PortfolioDashboard = () => {
  const { selectedAccount } = useAccount();
  const isAllAccounts = selectedAccount === "All Accounts";
  const history = useHistory();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [balance, setBalance]   = useState(null);
  const [filter, setFilter]     = useState("");
  const [sort, setSort]         = useState({ col: "stock_pnl", dir: "desc" });
  const [hiddenCols, setHiddenCols] = useState(() => {
    try {
      const saved = localStorage.getItem("portfolio_hidden_cols");
      if (saved) return new Set(JSON.parse(saved));
    } catch (_) {}
    return DEFAULT_HIDDEN;
  });
  const [colOrder, setColOrder] = useState(() => {
    try {
      const saved = localStorage.getItem("portfolio_col_order");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge: keep saved order, append any new columns not in saved
        const known = new Set(parsed);
        const merged = [...parsed, ...DEFAULT_COL_ORDER.filter(k => !known.has(k))];
        return merged;
      }
    } catch (_) {}
    return DEFAULT_COL_ORDER;
  });
  const [colPickerOpen, setColPickerOpen] = useState(false);

  // Drag-and-drop refs
  const dragKey = useRef(null);
  const dragOver = useRef(null);

  const onDragStart = (key) => { dragKey.current = key; };
  const onDragEnter = (key) => { dragOver.current = key; };
  const onDragEnd   = () => {
    if (!dragKey.current || !dragOver.current || dragKey.current === dragOver.current) return;
    setColOrder(prev => {
      const next = [...prev];
      const from = next.indexOf(dragKey.current);
      const to   = next.indexOf(dragOver.current);
      if (from === -1 || to === -1) return prev;
      next.splice(from, 1);
      next.splice(to, 0, dragKey.current);
      localStorage.setItem("portfolio_col_order", JSON.stringify(next));
      return next;
    });
    dragKey.current  = null;
    dragOver.current = null;
  };

  const toggleCol = (key) => {
    setHiddenCols(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      localStorage.setItem("portfolio_hidden_cols", JSON.stringify([...next]));
      return next;
    });
  };
  const show = (key) => !hiddenCols.has(key);

  const cycleSort = (col) => {
    setSort(s => s.col === col ? { col, dir: s.dir === "desc" ? "asc" : "desc" } : { col, dir: "desc" });
  };
  const sortArrow = (col) => sort.col === col ? (sort.dir === "desc" ? " ▼" : " ▲") : " ↕";

  useEffect(() => {
    if (!selectedAccount || isAllAccounts) return;
    setLoading(true);
    axios.get("/dashboard/summary", { params: { username: selectedAccount } })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
    // Fetch trading balance from broker (non-blocking)
    axios.get("/dashboard/balance", { params: { username: selectedAccount } })
      .then(r => setBalance(r.data))
      .catch(() => setBalance(null));
  }, [selectedAccount]);

  const stocks = (data?.stocks || [])
    .filter(s => s.stock_name.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => {
      let av, bv;
      if (sort.col === "stock_name")    { av = a.stock_name;    bv = b.stock_name;    return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av); }
      if (sort.col === "strategy_type") { av = a.strategy_type || ""; bv = b.strategy_type || ""; return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av); }
      if (sort.col === "stock_pnl")         { av = a.stock_pnl;         bv = b.stock_pnl; }
      if (sort.col === "option_pnl")        { av = a.fo_unrealised_pnl  ?? (sort.dir === "desc" ? -Infinity : Infinity); bv = b.fo_unrealised_pnl  ?? (sort.dir === "desc" ? -Infinity : Infinity); }
      if (sort.col === "qty")               { av = a.quantity ?? 0;      bv = b.quantity ?? 0; }
      if (sort.col === "invested")          { av = a.invested;           bv = b.invested; }
      if (sort.col === "nearest_put_pct")   { av = a.nearest_put_pct  ?? Infinity; bv = b.nearest_put_pct  ?? Infinity; }
      if (sort.col === "nearest_call_pct")  { av = a.nearest_call_pct ?? Infinity; bv = b.nearest_call_pct ?? Infinity; }
      if (sort.col === "booked_profit")     { av = a.booked_profit ?? (sort.dir === "desc" ? -Infinity : Infinity); bv = b.booked_profit ?? (sort.dir === "desc" ? -Infinity : Infinity); }
      if (sort.col === "expiry") {
        // MM/DD/YYYY → sortable YYYYMM string
        const toYYYYMM = (d) => { if (!d) return ""; const p = d.split("/"); return p.length === 3 ? p[2] + p[0] : ""; };
        return sort.dir === "asc" ? toYYYYMM(a.expiry).localeCompare(toYYYYMM(b.expiry)) : toYYYYMM(b.expiry).localeCompare(toYYYYMM(a.expiry));
      }
      return sort.dir === "asc" ? av - bv : bv - av;
    });

  const { option_metrics, totals } = data || {};

  return (
    <Fragment>
      <PageTitleAlt2
        heading="Portfolio Positions"
        subheading={isAllAccounts ? "Positions across all accounts" : "All stock positions with live P&L and option summary"}
        icon="pe-7s-note2 icon-gradient bg-sunny-morning"
      />

      {isAllAccounts ? (
        <AllAccountsPositions />
      ) : (
      <div style={{ padding: "0 25px 30px" }}>

        {/* ── Trading balance strip — always visible, loads independently ── */}
        <Row className="mb-2">
          {balance && balance.available_cash != null ? (
            <>
              <Col md={4}>
                <Card style={{ borderRadius: 10, border: "2px solid #28a745", background: "#f6fff8" }}>
                  <CardBody style={{ padding: "10px 16px" }}>
                    <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", marginBottom: 2 }}>Available Cash</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#28a745" }}>Rs.{fmt(balance.available_cash)}</div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4}>
                <Card style={{ borderRadius: 10, border: "1px solid #eee" }}>
                  <CardBody style={{ padding: "10px 16px" }}>
                    <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 2 }}>Used Margin</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#dc3545" }}>Rs.{fmt(balance.used_margin)}</div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4}>
                <Card style={{ borderRadius: 10, border: "1px solid #eee" }}>
                  <CardBody style={{ padding: "10px 16px" }}>
                    <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 2 }}>Total Margin</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#495057" }}>Rs.{fmt(balance.total_margin)}</div>
                  </CardBody>
                </Card>
              </Col>
            </>
          ) : (
            <Col>
              <Card style={{ borderRadius: 10, border: "1px solid #eee", background: "#fafafa" }}>
                <CardBody style={{ padding: "10px 16px" }}>
                  <div style={{ fontSize: 12, color: "#aaa" }}>
                    Trading balance unavailable (broker offline or session expired)
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>

        {/* ── P&L Summary strip ── */}
        {data && (
          <Row className="mb-3">
            {[
              { label: "Stock P&L",     value: pnlFmt(totals.stock_pnl),                color: pnlColor(totals.stock_pnl) },
              { label: "F&O P&L",       value: pnlFmt(option_metrics.fo_unrealised_pnl),  color: pnlColor(option_metrics.fo_unrealised_pnl) },
              { label: "Potential P&L", value: pnlFmt(option_metrics.potential_pnl), color: pnlColor(option_metrics.potential_pnl) },
              { label: "Short Value",   value: `Rs.${fmt(option_metrics.short_option_value)}`, color: "#6f42c1" },
            ].map(c => (
              <Col md={3} key={c.label}>
                <Card className="mb-3" style={{ borderRadius: 10, border: "1px solid #eee" }}>
                  <CardBody style={{ padding: "12px 16px" }}>
                    <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase" }}>{c.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: c.color }}>{c.value}</div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {/* ── Filter + count ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filter by stock name…"
            style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #ccc", fontSize: 13, width: 220 }}
          />
          <span style={{ fontSize: 13, color: "#888" }}>
            {loading ? "Loading…" : `${stocks.length} position${stocks.length !== 1 ? "s" : ""}`}
          </span>
          <div style={{ position: "relative", marginLeft: "auto" }}>
            <button
              onClick={() => setColPickerOpen(o => !o)}
              style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #ccc", fontSize: 12, cursor: "pointer", background: "#fff" }}
            >
              Columns ▾
            </button>
            {colPickerOpen && (
              <div style={{
                position: "absolute", right: 0, top: "110%", zIndex: 200,
                background: "#fff", border: "1px solid #ddd", borderRadius: 8,
                boxShadow: "0 4px 16px rgba(0,0,0,0.12)", padding: "10px 16px",
                minWidth: 180,
              }}>
                {TOGGLE_COLS.map(c => (
                  <label key={c.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
                    <input type="checkbox" checked={!hiddenCols.has(c.key)} onChange={() => toggleCol(c.key)} />
                    {c.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {loading && <div style={{ textAlign: "center", padding: 40 }}><Spinner color="primary" /></div>}

        {/* ── Positions table ── */}
        {!loading && (
          <Card style={{ borderRadius: 12, border: "1px solid #eee" }}>
            <CardBody style={{ padding: 0 }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f8f9fb" }}>
                      {/* Fixed: Stock */}
                      <th style={{ padding: "11px 14px", textAlign: "left", color: "#007bff", fontWeight: 600, whiteSpace: "nowrap", borderBottom: "2px solid #eee", cursor: "pointer", userSelect: "none" }}
                          onClick={() => cycleSort("stock_name")}>
                        Stock{sortArrow("stock_name")}
                      </th>
                      {/* Draggable middle columns in user-defined order */}
                      {colOrder.filter(k => show(k)).map(k => {
                        const def = TOGGLE_COLS.find(c => c.key === k);
                        if (!def) return null;
                        const sortCol = k === "qty" ? "qty" : k === "strategy" ? "strategy_type" : k === "option_pnl" ? "option_pnl" : ["stock_pnl","invested","nearest_put_pct","nearest_call_pct","booked_profit","expiry"].includes(k) ? k : null;
                        const isDragging = dragOver.current === k;
                        return (
                          <th key={k}
                              draggable
                              onDragStart={() => onDragStart(k)}
                              onDragEnter={() => onDragEnter(k)}
                              onDragOver={e => e.preventDefault()}
                              onDragEnd={onDragEnd}
                              onClick={sortCol ? () => cycleSort(sortCol) : undefined}
                              style={{
                                padding: "11px 14px", textAlign: "left",
                                color: sortCol ? "#007bff" : "#555",
                                fontWeight: 600, whiteSpace: "nowrap",
                                borderBottom: "2px solid #eee",
                                cursor: "grab", userSelect: "none",
                                background: isDragging ? "#e8f0fe" : undefined,
                                borderLeft: isDragging ? "2px solid #4285f4" : undefined,
                                opacity: dragKey.current === k ? 0.5 : 1,
                              }}>
                            ⠿ {def.label}{sortCol ? sortArrow(sortCol) : ""}
                          </th>
                        );
                      })}
                      {/* Fixed: Detail */}
                      <th style={{ padding: "11px 14px", borderBottom: "2px solid #eee" }} />
                    </tr>
                  </thead>
                  <tbody>
                    {stocks.length === 0 && (
                      <tr>
                        <td colSpan={15} style={{ textAlign: "center", padding: 32, color: "#aaa" }}>
                          No positions found.
                        </td>
                      </tr>
                    )}
                    {stocks.map(s => {
                      const fmtDelta = (v) => v == null ? "—" : v.toFixed(2);
                      const deltaColor = (v) => v == null ? "#ccc" : v >= 0 ? "#28a745" : "#dc3545";
                      return (
                      <tr
                        key={s.stock_name}
                        style={{ borderTop: "1px solid #f0f0f0" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                        onMouseLeave={e => e.currentTarget.style.background = ""}
                      >
                        <td style={{ padding: "10px 14px", fontWeight: 600 }}>
                          <span
                            style={{ color: "#007bff", cursor: "pointer" }}
                            onClick={() => window.open(`/trading/${s.stock_name}`, "_blank")}
                          >
                            {s.stock_name}
                          </span>
                        </td>
                        {colOrder.filter(k => show(k)).map(k => {
                          switch (k) {
                            case "ltp":
                              return <td key={k} style={{ padding: "10px 14px" }}>₹{s.ltp.toFixed(2)}</td>;
                            case "qty":
                              return (
                                <td key={k} style={{ padding: "10px 14px" }}>
                                  {s.quantity.toLocaleString()}
                                  {s.lots != null && <span style={{ color: "#888", fontSize: 11, marginLeft: 5 }}>({s.lots}L)</span>}
                                </td>
                              );
                            case "avg_price":
                              return <td key={k} style={{ padding: "10px 14px" }}>₹{s.avg_price.toFixed(2)}</td>;
                            case "invested":
                              return <td key={k} style={{ padding: "10px 14px", color: "#555" }}>₹{fmt(s.invested)}</td>;
                            case "stock_pnl":
                              return <td key={k} style={{ padding: "10px 14px", fontWeight: 600, color: pnlColor(s.stock_pnl) }}>{pnlFmt(s.stock_pnl)}</td>;
                            case "option_pnl": {
                              const hasFo = s.option_count > 0 || !!s.futures_pnl;
                              return <td key={k} style={{ padding: "10px 14px", color: hasFo ? pnlColor(s.fo_unrealised_pnl) : "#ccc" }}>{hasFo ? pnlFmt(s.fo_unrealised_pnl) : "—"}</td>;
                            }
                            case "potential_pnl":
                              return <td key={k} style={{ padding: "10px 14px", color: s.option_count > 0 ? pnlColor(s.potential_pnl) : "#ccc" }}>{s.option_count > 0 ? pnlFmt(s.potential_pnl) : "—"}</td>;
                            case "premium_rec":
                              return <td key={k} style={{ padding: "10px 14px", color: s.option_count > 0 ? "#6f42c1" : "#ccc" }}>{s.option_count > 0 ? `₹${fmt(s.premium_received)}` : "—"}</td>;
                            case "options":
                              return <td key={k} style={{ padding: "10px 14px" }}>{s.option_count > 0 ? <Badge color="info" pill>{s.option_count}</Badge> : <span style={{ color: "#ccc" }}>—</span>}</td>;
                            case "strategy":
                              return (
                                <td key={k} style={{ padding: "10px 14px" }}>
                                  {s.strategy_type ? (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                      <span style={{ fontSize: 11, fontWeight: 600, color: "#2c3e50", whiteSpace: "nowrap" }}>{strategyLabel(s.strategy_type)}</span>
                                      <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 8, display: "inline-block", width: "fit-content", background: s.strategy_status === "ACTIVE" ? "#d4edda" : "#e2e3e5", color: s.strategy_status === "ACTIVE" ? "#28a745" : "#6c757d" }}>{s.strategy_status}</span>
                                    </div>
                                  ) : <span style={{ color: "#ccc" }}>—</span>}
                                </td>
                              );
                            case "bullish_delta":
                              return <td key={k} style={{ padding: "10px 14px", color: deltaColor(s.bullish_delta), fontWeight: 600 }}>{s.bullish_delta != null ? `+${fmtDelta(s.bullish_delta)}` : "—"}</td>;
                            case "bearish_delta":
                              return <td key={k} style={{ padding: "10px 14px", color: deltaColor(s.bearish_delta), fontWeight: 600 }}>{fmtDelta(s.bearish_delta)}</td>;
                            case "delta_diff":
                              return <td key={k} style={{ padding: "10px 14px", color: deltaColor(s.delta_diff), fontWeight: 600 }}>{s.delta_diff != null ? (s.delta_diff >= 0 ? `+${fmtDelta(s.delta_diff)}` : fmtDelta(s.delta_diff)) : "—"}</td>;
                            case "nearest_put_pct":
                              return (
                                <td key={k} style={{ padding: "10px 14px", fontWeight: 600, color: distColor(s.nearest_put_pct) }}>
                                  {s.nearest_put_pct != null ? <span style={s.nearest_put_pct < 0 ? { background: "#fde2e2", color: "#dc3545", padding: "1px 6px", borderRadius: 6 } : undefined}>{fmtDist(s.nearest_put_pct)}</span> : "—"}
                                </td>
                              );
                            case "nearest_call_pct":
                              return (
                                <td key={k} style={{ padding: "10px 14px", fontWeight: 600, color: distColor(s.nearest_call_pct) }}>
                                  {s.nearest_call_pct != null ? <span style={s.nearest_call_pct < 0 ? { background: "#fde2e2", color: "#dc3545", padding: "1px 6px", borderRadius: 6 } : undefined}>{fmtDist(s.nearest_call_pct)}</span> : "—"}
                                </td>
                              );
                            case "booked_profit":
                              return <td key={k} style={{ padding: "10px 14px", fontWeight: 600, color: s.booked_profit != null ? pnlColor(s.booked_profit) : "#ccc" }}>{s.booked_profit != null ? pnlFmt(s.booked_profit) : "—"}</td>;
                            case "expiry":
                              return <td key={k} style={{ padding: "10px 14px", color: "#555", whiteSpace: "nowrap" }}>{fmtExpMonth(s.expiry)}</td>;
                            default: return null;
                          }
                        })}
                        <td style={{ padding: "10px 14px" }}>
                          <span
                            style={{ color: "#007bff", cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" }}
                            onClick={() => window.open(`/trading/${s.stock_name}`, "_blank")}
                          >
                            Detail →
                          </span>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
      )}
    </Fragment>
  );
};

export default PortfolioDashboard;
